/**
 * Registro del protocolo PMTiles y construcción del estilo base.
 *
 * MapLibre no sabe leer `pmtiles://` por sí solo: es un esquema propio que
 * resuelve un protocolo registrado, el cual traduce cada petición de tesela a
 * una petición de RANGO sobre el archivo único. Sin registrarlo, MapLibre
 * intenta un fetch del esquema desconocido y falla con «Failed to fetch»
 * dejando el mapa vacío.
 */

import maplibregl, { type StyleSpecification } from 'maplibre-gl';
import { Protocol } from 'pmtiles';

let registered = false;

/** Idempotente: MapLibre rechaza registrar dos veces el mismo protocolo. */
export function registerPmtiles(): void {
  if (registered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  registered = true;
}

/**
 * URL del PMTiles base. Se normaliza a absoluta porque el protocolo la usa tal
 * cual para construir las peticiones de rango, y una ruta relativa como
 * `/tiles/x.pmtiles` no resuelve a un origen.
 */
export function tilesUrl(): string {
  const raw = (import.meta.env['VITE_TILES_PMTILES_URL'] as string | undefined) ?? '';
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return new URL(raw, window.location.origin).toString();
}

/**
 * URL de los glifos tipográficos.
 *
 * Son un recurso APARTE de las teselas: PMTiles guarda geometría, no fuentes.
 * MapLibre exige `glyphs` en el estilo para poder dibujar CUALQUIER capa de
 * tipo `symbol` con `text-field`; sin él, el estilo falla al cargar y no se ve
 * nada, ni siquiera las capas que no llevan texto.
 *
 * Por eso las etiquetas de calle son opcionales: si no hay glifos configurados,
 * el estilo se construye sin la capa de texto en lugar de romperse.
 */
export function glyphsUrl(): string {
  return (import.meta.env['VITE_TILES_GLYPHS_URL'] as string | undefined) ?? '';
}

/**
 * Estilo de la capa base.
 *
 * Degrada en dos escalones, de modo que el mapa siempre muestre algo:
 *  1. Con teselas y glifos: calles, edificios, agua y nombres de calle.
 *  2. Con teselas y sin glifos: lo mismo sin nombres.
 *  3. Sin teselas: fondo liso, sobre el que igual se dibuja el polígono del
 *     área asignada y la posición del encuestador.
 */
export function buildBaseStyle(): StyleSpecification {
  const tiles = tilesUrl();
  const glyphs = glyphsUrl();

  const background: StyleSpecification['layers'][number] = {
    id: 'fondo',
    type: 'background',
    paint: { 'background-color': '#eef2f7' },
  };

  if (!tiles) {
    return { version: 8, sources: {}, layers: [background] };
  }

  const layers: StyleSpecification['layers'] = [
    background,
    {
      id: 'agua',
      type: 'fill',
      source: 'base',
      'source-layer': 'water',
      paint: { 'fill-color': '#bfdbfe' },
    },
    {
      id: 'uso-suelo',
      type: 'fill',
      source: 'base',
      'source-layer': 'landuse',
      paint: { 'fill-color': '#e7ecf2' },
    },
    {
      id: 'edificios',
      type: 'fill',
      source: 'base',
      'source-layer': 'building',
      paint: { 'fill-color': '#dbe2ea', 'fill-outline-color': '#c3ccd8' },
    },
    {
      id: 'calles',
      type: 'line',
      source: 'base',
      'source-layer': 'transportation',
      paint: { 'line-color': '#ffffff', 'line-width': 2 },
    },
    {
      id: 'calles-borde',
      type: 'line',
      source: 'base',
      'source-layer': 'transportation',
      paint: { 'line-color': '#9aa5b1', 'line-width': 0.6 },
    },
  ];

  if (glyphs) {
    layers.push({
      id: 'nombres-calles',
      type: 'symbol',
      source: 'base',
      'source-layer': 'transportation_name',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'symbol-placement': 'line',
      },
      paint: { 'text-color': '#475569', 'text-halo-color': '#fff', 'text-halo-width': 1.2 },
    });
  }

  return {
    version: 8,
    ...(glyphs ? { glyphs } : {}),
    sources: { base: { type: 'vector', url: `pmtiles://${tiles}` } },
    layers,
  };
}

/**
 * Textos de la interfaz de MapLibre en español.
 *
 * MapLibre los trae en inglés por omisión, y el aviso de gestos cooperativos
 * («Use ⌘ + scroll to zoom the map») aparece encima del mapa cada vez que
 * alguien intenta desplazar la página: es el texto más visible de todo el
 * componente y no puede quedar en otro idioma que el resto de la aplicación.
 */
export const MAP_LOCALE: Record<string, string> = {
  'CooperativeGesturesHandler.WindowsHelpText': 'Usa Ctrl + rueda para acercar el mapa',
  'CooperativeGesturesHandler.MacHelpText': 'Usa ⌘ + rueda para acercar el mapa',
  'CooperativeGesturesHandler.MobileHelpText': 'Usa dos dedos para mover el mapa',
  'NavigationControl.ZoomIn': 'Acercar',
  'NavigationControl.ZoomOut': 'Alejar',
  'NavigationControl.ResetBearing': 'Restablecer orientación',
  'AttributionControl.ToggleAttribution': 'Mostrar atribución',
  'Map.Title': 'Mapa',
  'Popup.Close': 'Cerrar',
  'ScaleControl.Meters': 'm',
  'ScaleControl.Kilometers': 'km',
};
